/**
 * Customer feedback — submit and track improvement requests.
 * Statuses (New → Under Review → Planned → Not Planned → Delivered) match
 * exactly what Suntronix product ops see in /governance.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  new:          "New",
  under_review: "Under review",
  planned:      "Planned",
  not_planned:  "Not planned",
  delivered:    "Delivered",
};

const STATUS_STYLE = {
  new:          "text-slate-700 bg-slate-50 border-slate-200",
  under_review: "text-sky-700 bg-sky-50 border-sky-200",
  planned:      "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
  not_planned:  "text-slate-500 bg-slate-50 border-slate-200",
  delivered:    "text-emerald-700 bg-emerald-50 border-emerald-200",
};

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/governance/feedback").then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-3xl space-y-6" data-testid="feedback-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Feedback loop</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Share what would make ONE better</h1>
        <p className="text-sm text-slate-600 mt-3 max-w-xl">
          Every idea gets a public status. We&apos;ll never mark something &ldquo;Delivered&rdquo; without shipping the work you asked for.
        </p>
      </div>

      <div className="flex gap-2">
        <button data-testid="feedback-new" onClick={() => setShowForm(true)} className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 flex items-center gap-1.5">
          <MessageSquarePlus className="h-4 w-4" /> Submit an idea
        </button>
        <button onClick={load} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {showForm && <FeedbackForm onClose={() => setShowForm(false)} onCreated={load} />}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-sm text-slate-500">
          Nothing submitted yet. Your first idea will appear here with a live status.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md divide-y">
          {items.map((f) => (
            <div key={f.id} className="p-4" data-testid={`feedback-item-${f.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_STYLE[f.status]}`}>{STATUS_LABEL[f.status]}</span>
                <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 bg-slate-50">{f.category}</span>
                <div className="text-sm font-semibold text-slate-900 ml-1">{f.title}</div>
              </div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">{f.description}</div>
              {f.response && (
                <div className="mt-2 text-xs text-slate-700 bg-slate-50 border-l-2 border-slate-300 pl-3 py-1">
                  <span className="font-medium">Product team: </span>{f.response}
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-2">Submitted {new Date(f.created_at).toLocaleString("en-GB")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackForm({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", category: "improvement" });
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Please add a title");
    try {
      await api.post("/governance/feedback", f);
      toast.success("Thanks — logged and visible in your tracker.");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="feedback-form">
      <label className="block">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Title</div>
        <input data-testid="feedback-title" className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </label>
      <label className="block">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Category</div>
        <select data-testid="feedback-category" className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          {["improvement", "bug", "question", "idea"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">What would help?</div>
        <textarea data-testid="feedback-description" className="input min-h-[100px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </label>
      <div className="flex gap-2 justify-end">
        <button className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" onClick={onClose}>Cancel</button>
        <button data-testid="feedback-submit" className="h-9 px-3 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800" onClick={submit}>Submit</button>
      </div>
    </div>
  );
}
